import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, DollarSign, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useContacts } from "@/hooks/useContacts";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const giftStatuses = ["planned", "purchased", "shipped", "delivered", "cancelled"];
const shippingStatuses = ["pending", "shipped", "delivered", "returned"];

interface GiftFormValues {
  name: string;
  description?: string;
  status: string;
  value?: number | null;
  shipping_status?: string;
  shipping_details?: string;
  date_sent?: Date | null;
  contact_id?: string | null;
  organization_id?: string | null;
  gift_id?: string | null;
  tracking_number?: string;
  occasion?: string;
  notes?: string;
}

interface EnhancedGiftFormProps {
  initialValues?: Partial<GiftFormValues>;
  onSubmit: (values: GiftFormValues) => Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export function EnhancedGiftForm({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = "Save Gift"
}: EnhancedGiftFormProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("planned");
  const [value, setValue] = useState("");
  const [shippingStatus, setShippingStatus] = useState("pending");
  const [shippingDetails, setShippingDetails] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [dateSent, setDateSent] = useState<Date | null>(new Date());
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [organizationId, setOrganizationId] = useState<string | undefined>(undefined);
  const [giftId, setGiftId] = useState<string | undefined>(undefined);
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");
  
  const { contacts, isLoading: contactsLoading } = useContacts();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch gift inventory items
  const { data: giftItems, isLoading: isLoadingGiftItems } = useQuery({
    queryKey: ['gift_inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gift_inventory')
        .select('*')
        .order('gift_name', { ascending: true });
        
      if (error) {
        throw new Error(error.message);
      }
      
      return data || [];
    },
  });

  // Initialize form with initial values if provided
  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setStatus(initialValues.status || "planned");
      setValue(initialValues.value ? initialValues.value.toString() : "");
      setShippingStatus(initialValues.shipping_status || "pending");
      setShippingDetails(initialValues.shipping_details || "");
      setTrackingNumber(initialValues.tracking_number || "");
      
      if (initialValues.date_sent) {
        setDateSent(new Date(initialValues.date_sent));
      }
      
      setContactId(initialValues.contact_id);
      setOrganizationId(initialValues.organization_id);
      setGiftId(initialValues.gift_id);
      setOccasion(initialValues.occasion || "");
      setNotes(initialValues.notes || "");
    }
  }, [initialValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !status) {
      return; // Form validation should handle this
    }
    
    const formValues: GiftFormValues = {
      name,
      description,
      status,
      value: value ? parseFloat(value) : null,
      shipping_status: shippingStatus,
      shipping_details: shippingDetails,
      tracking_number: trackingNumber,
      date_sent: dateSent,
      contact_id: contactId ? contactId : null,
      organization_id: organizationId ? organizationId : null,
      gift_id: giftId ? giftId : null,
      occasion,
      notes,
    };
    
    await onSubmit(formValues);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Gift Name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Gift name"
            required
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="gift_id">Gift Inventory Item</Label>
            <Select 
              value={giftId} 
              onValueChange={setGiftId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select gift item" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Custom Gift</SelectItem>
                {isLoadingGiftItems ? (
                  <div className="flex items-center justify-center p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  giftItems?.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.gift_name} (${item.cost})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="value">Value</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="value"
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0.00"
                className="pl-9"
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="status">Gift Status *</Label>
            <Select value={status} onValueChange={setStatus} required>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {giftStatuses.map((statusOption) => (
                  <SelectItem key={statusOption} value={statusOption}>
                    {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="shipping_status">Shipping Status</Label>
            <Select 
              value={shippingStatus} 
              onValueChange={setShippingStatus}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select shipping status" />
              </SelectTrigger>
              <SelectContent>
                {shippingStatuses.map((statusOption) => (
                  <SelectItem key={statusOption} value={statusOption}>
                    {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="date_sent">Date Sent</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date_sent"
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateSent && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateSent ? format(dateSent, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateSent || undefined}
                onSelect={(date) => setDateSent(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contact_id">Recipient Contact</Label>
            <Select 
              value={contactId} 
              onValueChange={setContactId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None</SelectItem>
                {contactsLoading ? (
                  <div className="flex items-center justify-center p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="organization_id">Recipient Organization</Label>
            <Select 
              value={organizationId} 
              onValueChange={setOrganizationId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None</SelectItem>
                {orgsLoading ? (
                  <div className="flex items-center justify-center p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="occasion">Occasion</Label>
          <Input
            id="occasion"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
            placeholder="e.g., Birthday, Holiday, Thank You"
          />
        </div>
        
        <div>
          <Label htmlFor="tracking_number">Tracking Number</Label>
          <Input
            id="tracking_number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Shipping tracking number"
          />
        </div>
        
        <div>
          <Label htmlFor="shipping_details">Shipping Details</Label>
          <Textarea
            id="shipping_details"
            value={shippingDetails}
            onChange={(e) => setShippingDetails(e.target.value)}
            placeholder="Shipping address and details"
            className="min-h-[80px]"
          />
        </div>
        
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description of the gift"
            className="min-h-[80px]"
          />
        </div>
        
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes"
            className="min-h-[80px]"
          />
        </div>
      </div>
      
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/gifts")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}